import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBetSlip } from "@/context/BetSlipContext";
import { useLiveDealerStatus } from "@/hooks/useLiveDealerStatus";
import DiscoveryGameCard from "@/components/sportsbook/DiscoveryGameCard";
import { sortSportsbookEvents } from "@/lib/sportsbookPriority";
import DailyRewardCard from "../ui/DailyRewardCard";

/* MobileHome — CONTENT PARITY with desktop Home (pages/Home.jsx), in the mobile
   look. Mirrors the REAL desktop sections (greeting, gated daily reward, the three
   feature cards, featured matchup, live now, free-bet tokens) off the SAME data
   hooks (AuthContext user/vip, /wallet.bonus, /casino/featured, /events, bet slip,
   live-dealer status). No fabricated data — honest TBD / cooldown / empty states.
   Default top app bar (this is a tab root, so no useChrome override). */

// Native game artwork (same assets the Casino lobby / desktop Home use) so the
// featured Casino card shows the right thumbnail per game.
const IMG = {
  keno: "/game-art/keno.jpeg",
  dice: "/game-art/dice.jpeg",
  coinflip: "/game-art/coinflip.jpeg",
  crash: "/game-art/crash.jpeg",
  mines: "/game-art/mines.jpeg",
  slots: "/game-art/slots.jpeg",
  roulette: "/game-art/roulette.jpeg",
  blackjack: "/game-art/blackjack.jpeg",
  baccarat: "/game-art/baccarat.jpeg",
  live_dealer: "/game-art/live_dealer.jpeg",
};
const NATIVE_CASINO = [
  { name: "Crash", img: IMG.crash, tag: "PRYMEBET Original" },
  { name: "Mines", img: IMG.mines, tag: "PRYMEBET Original" },
  { name: "Dice", img: IMG.dice, tag: "PRYMEBET Original" },
  { name: "Keno", img: IMG.keno, tag: "PRYMEBET Original" },
  { name: "Blackjack", img: IMG.blackjack, tag: "PRYMEBET Original" },
  { name: "Slots", img: IMG.slots, tag: "PRYMEBET Original" },
  { name: "Roulette", img: IMG.roulette, tag: "PRYMEBET Original" },
  { name: "Coin Flip", img: IMG.coinflip, tag: "PRYMEBET Original" },
  { name: "Baccarat", img: IMG.baccarat, tag: "PRYMEBET Original" },
];
// Deep-link slugs so each Casino CTA opens the exact native game (same map as desktop Home).
const GAME_SLUG = { crash: "crash", mines: "mines", dice: "dice", blackjack: "blackjack", slots: "slots", roulette: "roulette", coinflip: "coinflip", baccarat: "baccarat" };
const pickRandom = (arr) => (arr.length ? arr[Math.floor(Math.random() * arr.length)] : null);

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

const isGameEvent = (e) => !["outright", "prop"].includes(e.event_type) && e.home_team && e.away_team;

export default function MobileHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toggle, has } = useBetSlip();
  const { anyLive: liveDealerLive } = useLiveDealerStatus();

  const [events, setEvents] = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [eventsErr, setEventsErr] = useState(false);
  const [featured, setFeatured] = useState({ casino: [], live: [] });

  // /events — same source the desktop Home + Sportsbook use.
  useEffect(() => {
    let alive = true;
    api.get("/events")
      .then(({ data }) => { if (alive) { setEvents(Array.isArray(data) ? data : []); setEventsLoaded(true); setEventsErr(false); } })
      .catch(() => { if (alive) setEventsErr(true); });
    return () => { alive = false; };
  }, []);

  // Casino / live-dealer preview cards.
  useEffect(() => {
    let alive = true;
    api.get("/casino/featured")
      .then(({ data }) => { if (alive) setFeatured({ casino: data?.casino || [], live: data?.live || [] }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const live = useMemo(() => sortSportsbookEvents(events.filter((e) => e.status === "live")), [events]);
  const upcoming = useMemo(() => sortSportsbookEvents(events.filter((e) => e.status === "open")), [events]);
  const liveGames = useMemo(() => live.filter(isGameEvent), [live]);

  // Featured matchup — a real live/open game with markets (same selection as desktop Home).
  const heroEvent = useMemo(() =>
    live.find((e) => isGameEvent(e) && (e.outcomes || []).length) ||
    upcoming.find((e) => isGameEvent(e) && (e.outcomes || []).length) || null,
  [live, upcoming]);

  // Casino feature-card pick — native originals + previewed provider titles (own thumbnail).
  const casinoPick = useMemo(() => {
    const provider = (featured.casino || [])
      .filter((g) => g.thumbnail_url)
      .map((g) => ({ name: g.name, img: g.thumbnail_url, tag: g.provider }));
    return pickRandom([...NATIVE_CASINO, ...provider]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featured.casino]);
  const casinoSlug = GAME_SLUG[String(casinoPick?.name || "").toLowerCase().replace(/\s+/g, "")] || "";

  // Live-dealer card — a native table if a dealer is broadcasting, else a previewed provider game.
  const livePick = useMemo(() => {
    if (liveDealerLive) return { name: "Main Blackjack", sub: "Blackjack & Baccarat", img: IMG.live_dealer, badge: "live" };
    const g = pickRandom((featured.live || []).filter((x) => x.thumbnail_url));
    if (g) return { name: g.name, sub: `${g.provider} · live dealer`, img: g.thumbnail_url, badge: "tables" };
    return { name: "Main Blackjack", sub: "Blackjack & Baccarat", img: IMG.live_dealer, badge: "offline" };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featured.live, liveDealerLive]);

  // Same global bet-slip wiring the Sportsbook/desktop Home use (copied leg shape).
  const pick = (event, outcome) => {
    const eventLabel = event.event_type === "outright" && event.title ? event.title : `${event.away_team} @ ${event.home_team}`;
    toggle({
      eventId: event.id, outcomeId: String(outcome.id), label: `${outcome.label}`, odds: outcome.odds,
      marketVersion: outcome.market_version ?? null, line: outcome.line ?? null, side: outcome.side ?? null, receivedAt: outcome.received_at ?? null, providerUpdatedAt: outcome.provider_updated_at ?? null, expiresAt: outcome.expires_at ?? null,
      eventLabel, market: outcome.market, league: event.league || null, sport: event.sport || null,
      live: event.status === "live",
    });
  };
  const isPicked = (eventId, outcomeId) => has(eventId, outcomeId);

  const name = user?.username || "player";
  const freeBets = user?.free_bets?.length || 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22, paddingTop: 14 }}>
      {/* Greeting */}
      <div style={{ padding: "0 16px" }}>
        <p style={{ font: "800 11px 'Outfit'", letterSpacing: ".16em", textTransform: "uppercase", color: "var(--m-green)" }}>Home</p>
        <h1 style={{ margin: "4px 0 0", font: "800 28px/1.05 'Manrope'", letterSpacing: "-.02em", color: "#fff" }}>
          {greeting()}, <span style={{ color: "var(--m-muted)" }}>{name}</span>
        </h1>
      </div>

      {/* Daily reward — shared claim container (identical on Casino). Claimable → Claim
          button; claimed → a live countdown replaces the button. */}
      <DailyRewardCard />

      {/* Feature cards — stacked on mobile: (a) Refer/Get coins, (b) Casino, (c) Live Dealer */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 16px" }}>
        {user?.first_purchase_done ? (
          <FeatureCard
            eyebrow="Refer a friend" title="Invite friends, earn rewards"
            sub="Earn a reward for every friend who joins and plays." cta="Share your code"
            onClick={() => navigate("/rewards#refer")}
          />
        ) : (
          <FeatureCard
            eyebrow="Get coins" title="Top up & play"
            sub="Pick a coin pack to play the sportsbook and casino. Free daily drops too." cta="See coin packs"
            onClick={() => navigate("/store")}
          />
        )}

        <FeatureCard
          eyebrow="Casino" title={casinoPick?.name || "Casino"}
          sub={casinoPick?.tag || "Provably fair"} cta="Play now"
          img={casinoPick?.img}
          onClick={() => navigate(casinoSlug ? `/casino?game=${casinoSlug}` : "/casino")}
        />

        <FeatureCard
          eyebrow="Live dealer" eyebrowMuted title={livePick?.name || "Main Blackjack"}
          sub={livePick?.sub || "Blackjack & Baccarat"} cta="Take a seat"
          img={livePick?.img}
          badge={livePick.badge}
          onClick={() => navigate("/casino?tab=live")}
        />
      </div>

      {/* Featured matchup */}
      {heroEvent && (
        <div style={{ padding: "0 16px" }}>
          <p style={{ margin: "0 0 8px", font: "800 10px 'Outfit'", letterSpacing: ".2em", textTransform: "uppercase", color: "var(--m-green)" }}>Featured matchup</p>
          <DiscoveryGameCard event={heroEvent} onPick={pick} isPicked={isPicked} mobile />
        </div>
      )}

      {/* Live now — live events only, as a vertical card list */}
      <Section
        title="Live now" badge={liveGames.length > 0 ? liveGames.length : null}
        actionLabel="Sportsbook" onAction={() => navigate("/sportsbook")}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 16px" }}>
          {liveGames.length > 0 ? (
            liveGames.slice(0, 5).map((ev) => (
              <DiscoveryGameCard key={ev.id} event={ev} onPick={pick} isPicked={isPicked} mobile />
            ))
          ) : (eventsErr && !eventsLoaded) ? (
            <EmptyRow>We couldn’t load the latest games. Open the Sportsbook to retry.</EmptyRow>
          ) : (
            <EmptyRow>No games are live right now — open the Sportsbook for upcoming games.</EmptyRow>
          )}
        </div>
      </Section>

      {/* Free bet tokens — honest count from user.free_bets */}
      <button
        type="button" onClick={() => navigate("/rewards")}
        style={{ margin: "0 16px", height: 60, boxSizing: "border-box", borderRadius: 16, padding: "0 10px 0 12px", display: "flex", alignItems: "center", gap: 12, textAlign: "left", border: 0, cursor: "pointer",
          background: "linear-gradient(180deg,#17171B 0%,#111114 100%)", boxShadow: "var(--m-elev-card)" }}
      >
        <span style={{ width: 40, height: 40, flex: "none", borderRadius: 12, background: freeBets > 0 ? "rgba(0,214,50,.12)" : "#1C1C21", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="rewards" size={20} style={{ color: freeBets > 0 ? "var(--m-green)" : "var(--m-muted-2)" }} />
        </span>
        <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ font: "700 14px 'Outfit'" }}>Free bet tokens</span>
          <span className="m-tnum" style={{ font: "500 12px 'Outfit'", color: "var(--m-muted)" }}>{freeBets} available</span>
        </span>
        <Icon name="chevron-right" size={18} style={{ color: "var(--m-faint)" }} />
      </button>
    </div>
  );
}

/* A stacked feature card — brand art wash, eyebrow, title, sub, CTA; optional live badge. */
function FeatureCard({ eyebrow, eyebrowMuted, title, sub, cta, img, badge, onClick }) {
  return (
    <button
      type="button" onClick={onClick}
      style={{ position: "relative", minHeight: 140, boxSizing: "border-box", borderRadius: 20, overflow: "hidden", textAlign: "left", border: 0, cursor: "pointer",
        background: "linear-gradient(180deg,#19191D 0%,#0F0F12 100%)", boxShadow: "var(--m-elev-door)" }}
    >
      {img && <img src={img} alt="" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: .4 }} onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      {!img && <div style={{ position: "absolute", inset: 0, background: "radial-gradient(90% 120% at 100% 0%,rgba(0,214,50,.14) 0%,rgba(0,214,50,0) 60%)" }} />}
      {img && <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,rgba(0,0,0,.25) 0%,rgba(0,0,0,.85) 100%)" }} />}
      <div style={{ position: "relative", padding: 16, display: "flex", flexDirection: "column", gap: 4, minHeight: 140, justifyContent: "flex-end" }}>
        {badge && (
          <span style={{ position: "absolute", right: 14, top: 14, display: "flex", alignItems: "center", gap: 5, height: 20, padding: "0 8px", borderRadius: 999,
            font: "800 10px 'Outfit'", letterSpacing: ".06em",
            background: badge === "live" ? "rgba(239,68,68,.15)" : badge === "tables" ? "rgba(0,214,50,.15)" : "rgba(255,255,255,.1)",
            color: badge === "live" ? "var(--m-red)" : badge === "tables" ? "var(--m-green)" : "rgba(255,255,255,.55)" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: badge === "live" ? "var(--m-red)" : badge === "tables" ? "var(--m-green)" : "rgba(255,255,255,.3)", animation: badge === "live" ? "pnrPulse 1.6s ease-out infinite" : undefined }} />
            {badge === "live" ? "LIVE" : badge === "tables" ? "TABLES" : "OFFLINE"}
          </span>
        )}
        <span style={{ font: "800 10px 'Outfit'", letterSpacing: ".18em", textTransform: "uppercase", color: eyebrowMuted ? "rgba(255,255,255,.7)" : "var(--m-green)" }}>{eyebrow}</span>
        <span style={{ font: "800 22px 'Manrope'", letterSpacing: "-.02em", color: "#fff", textShadow: img ? "0 1px 6px rgba(0,0,0,.8)" : "none" }}>{title}</span>
        <span style={{ font: "500 12px 'Outfit'", color: img ? "rgba(255,255,255,.85)" : "var(--m-muted)" }}>{sub}</span>
        <span style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, font: "700 13px 'Outfit'", color: "#fff" }}>
          {cta} <Icon name="arrow-right" size={14} />
        </span>
      </div>
    </button>
  );
}

function Section({ title, badge, actionLabel, onAction, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px" }}>
        <span role="heading" aria-level={2} style={{ display: "flex", alignItems: "center", gap: 8, font: "800 17px 'Manrope'", letterSpacing: "-.02em" }}>
          {title}
          {badge != null && (
            <span className="m-tnum" style={{ display: "inline-flex", alignItems: "center", gap: 5, height: 20, padding: "0 8px", borderRadius: 999, font: "800 11px 'Outfit'", background: "rgba(239,68,68,.12)", color: "var(--m-red)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.32)" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--m-red)" }} />{badge}
            </span>
          )}
        </span>
        {actionLabel && (
          <button type="button" onClick={onAction} aria-label={actionLabel} style={{ display: "flex", alignItems: "center", gap: 2, padding: "8px 0 8px 8px", font: "600 13px 'Outfit'", color: "var(--m-green)", background: "none", border: 0, cursor: "pointer" }}>
            {actionLabel}<Icon name="chevron-right" size={14} style={{ color: "var(--m-green)" }} />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyRow({ children }) {
  return (
    <div style={{ borderRadius: 16, padding: "22px 16px", textAlign: "center", font: "500 13px 'Outfit'", color: "var(--m-muted)", background: "#111114", boxShadow: "inset 0 0 0 1px var(--m-hair)" }}>
      {children}
    </div>
  );
}
